// ABOUTME: Error page react-router renders in place of a route whose element failed to load or render.
// ABOUTME: Tells stale-build chunk failures apart from other errors and offers reload / home actions.
import {
    Box,
    Button,
    Collapse,
    ErrorOutlineIcon,
    HomeIcon,
    RefreshIcon,
    Stack,
    Typography,
} from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

// Messages the browsers emit when a lazily imported page chunk cannot be fetched, which happens
// when a new build replaced the hashed chunks the running app still points to.
const CHUNK_LOAD_ERROR_PATTERNS = [
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /Loading (CSS )?chunk [\w-]+ failed/i,
];

type ErrorDetails = {
    message: string;
    stack?: string;
    isChunkLoadError: boolean;
    isNotFound: boolean;
};

const describeError = (error: unknown): ErrorDetails => {
    if (isRouteErrorResponse(error)) {
        return {
            message: `${error.status} ${error.statusText}`,
            stack: typeof error.data === 'string' ? error.data : undefined,
            isChunkLoadError: false,
            isNotFound: error.status === 404,
        };
    }
    const message = error instanceof Error ? error.message : String(error);
    return {
        message,
        stack: error instanceof Error ? error.stack : undefined,
        isChunkLoadError: CHUNK_LOAD_ERROR_PATTERNS.some(pattern => pattern.test(message)),
        isNotFound: false,
    };
};

export const RouteErrorPage = () => {
    const error = useRouteError();
    const navigate = useNavigate();
    const { formatMessage } = useIntl();
    const [showDetails, setShowDetails] = useState(false);

    const { message, stack, isChunkLoadError, isNotFound } = describeError(error);

    const title = (() => {
        if (isChunkLoadError) {
            return formatMessage({
                id: 'route-error.chunk-load.title',
                defaultMessage: 'The application has been updated',
            });
        }
        if (isNotFound) {
            return formatMessage({
                id: 'route-error.not-found.title',
                defaultMessage: 'Page not found',
            });
        }
        return formatMessage({
            id: 'route-error.generic.title',
            defaultMessage: 'Something went wrong',
        });
    })();

    const description = (() => {
        if (isChunkLoadError) {
            return formatMessage({
                id: 'route-error.chunk-load.description',
                defaultMessage:
                    'A new version of the application is available. Reload the page to continue.',
            });
        }
        if (isNotFound) {
            return formatMessage({
                id: 'route-error.not-found.description',
                defaultMessage: 'The page you are looking for does not exist or has been moved.',
            });
        }
        return formatMessage({
            id: 'route-error.generic.description',
            defaultMessage:
                'An unexpected error occurred while loading this page. Reload the page or go back to the home page.',
        });
    })();

    return (
        <Stack
            direction="column"
            alignItems="center"
            justifyContent="center"
            gap={2}
            height={1}
            minHeight="60vh"
            padding={4}
            textAlign="center"
        >
            <Box color="error.main" fontSize={64} lineHeight={0}>
                <ErrorOutlineIcon fontSize="inherit" />
            </Box>
            <Typography variant="h4">{title}</Typography>
            <Typography variant="body1" color="text.secondary" maxWidth={560}>
                {description}
            </Typography>

            <Stack direction="row" gap={1} marginTop={1}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RefreshIcon />}
                    onClick={() => window.location.reload()}
                >
                    {formatMessage({
                        id: 'route-error.action.reload',
                        defaultMessage: 'Reload page',
                    })}
                </Button>
                <Button variant="text" startIcon={<HomeIcon />} onClick={() => navigate('/')}>
                    {formatMessage({
                        id: 'route-error.action.home',
                        defaultMessage: 'Go to home page',
                    })}
                </Button>
            </Stack>

            <Box width={1} maxWidth={800}>
                <Button variant="text" size="small" onClick={() => setShowDetails(v => !v)}>
                    {showDetails
                        ? formatMessage({
                              id: 'route-error.action.hide-details',
                              defaultMessage: 'Hide details',
                          })
                        : formatMessage({
                              id: 'route-error.action.show-details',
                              defaultMessage: 'Show details',
                          })}
                </Button>
                <Collapse in={showDetails}>
                    <Box
                        component="pre"
                        textAlign="left"
                        fontSize={12}
                        padding={2}
                        bgcolor="grey.100"
                        borderRadius={1}
                        overflow="auto"
                        whiteSpace="pre-wrap"
                    >
                        {stack || message}
                    </Box>
                </Collapse>
            </Box>
        </Stack>
    );
};
